import React, { useState, useEffect } from 'react';
import { Card, Collapse, IconButton, Grid, TableCell, TableRow, Box } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import StageExpandableTable from '../tabels/StageExpandableTable';
import Icon from '@mui/material/Icon';
import { grey } from '@mui/material/colors';
import PropTypes from 'prop-types';
import ProductList from './ProductList';
import DiffRenderer from './DiffRenderer';
import { useVisibility } from 'context/visibilityContext'; // Import visibility hook

const NestedItemGroup = ({ title, items, sObjectName, expandedStates, onToggle, parentOrg, showSameFields }) => {
  const hasItems = items && Object.values(items).some((arr) => arr.length > 0);
  if (!hasItems) {
    return null;
  }

  const renderItem = (item) => {
    const isExpanded = !!expandedStates[item.id];
    const nestedArrays = Object.entries(item).filter(
      ([key, value]) => value && typeof value === 'object' && value.hasOwnProperty('new')
    );

    return (
      <Card variant="outlined" sx={{ mt: 1 }} key={item.id}>
        <ArgonBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          onClick={() => onToggle(item.id)}
          sx={{ cursor: 'pointer', p: 1 }}
        >
          <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
            {item.item}
          </ArgonTypography>
          <IconButton size="small">
            <Icon>{isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
          </IconButton>
        </ArgonBox>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <ArgonBox p={2} bgcolor={grey[50]}>
            <DiffRenderer
              changes={item.diffs}
              sObjectName={sObjectName}
              orgType={parentOrg.type.toLowerCase()}
              showSameFields={showSameFields}
            />
            {nestedArrays.map(([key, value]) => {
              const nestedSObjectName =
                {
                  jeopardyRules: 'JeopardyRule',
                  falloutRules: 'FalloutRule',
                  dependencies: 'FulfillmentStepDependency',
                  fulfillmentStepDefinitions: 'FulfillmentStepDefinition',
                  fulfillmentStepDefinitionGroup: 'FulfillmentStepDefinitionGroup',
                  valueTransformations: 'ValueTransformation',
                  productDecompEnrichmentRules: 'ProductDecompEnrichmentRule',
                }[key] || key;
              return (
                <NestedItemGroup
                  key={key}
                  title={key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                  items={value}
                  sObjectName={nestedSObjectName}
                  expandedStates={expandedStates}
                  onToggle={onToggle}
                  parentOrg={parentOrg}
                  showSameFields={showSameFields}
                />
              );
            })}
          </ArgonBox>
        </Collapse>
      </Card>
    );
  };

  return (
    <ArgonBox mt={2} pl={2} sx={{ borderLeft: `2px solid ${grey[200]}` }}>
      <ArgonTypography variant="subtitle2" fontWeight="bold" color="secondary" mb={1}>
        {title}
      </ArgonTypography>
      {['new', 'changed', 'deleted', 'same'].map((type) => items[type]?.map((item) => renderItem(item)))}
    </ArgonBox>
  );
};

function FulfillmentReview({ selectedOrg, productData, showSameFields }) {
  const [expandedStates, setExpandedStates] = useState({});
  const [productDataMap, setProductDataMap] = useState({});
  const [selectedProductId, setSelectedProductId] = useState(null);
  const { visibilityConfig } = useVisibility();

  const columns = [
    { name: 'item', align: 'left' },
    { name: 'actions', align: 'right', width: '50px' },
  ];

  const processDiffArray = (diffArray, sObjectName) => {
    const config = visibilityConfig.rc?.find((c) => c.objectName === sObjectName);
    const displayKeys = config?.displayColumns
      ? config.displayColumns.split(',').map((s) => s.trim())
      : ['name', 'Name'];

    const result = { new: [], changed: [], deleted: [], same: [] };
    ['new', 'changed', 'deleted', 'same'].forEach((type) => {
      result[type] = (diffArray?.[type] || []).map((item) => {
        const fields = item.fields || item;
        const displayName = displayKeys
          .map((key) => fields[key])
          .filter((v) => v != null)
          .join(' - ');

        const processedItem = {
          id: item.recordId || item.id || `${sObjectName}-${Math.random()}`,
          item:
            displayName ||
            `${sObjectName.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())} - ${item.recordId}`,
          diffs: item.changes,
        };

        // Recursively process any nested arrays found on the item
        Object.keys(item).forEach((key) => {
          if (item[key] && typeof item[key] === 'object' && item[key].hasOwnProperty('new')) {
            const nestedSObjectName = key.charAt(0).toUpperCase() + key.slice(1);
            processedItem[key] = processDiffArray(item[key], nestedSObjectName);
          }
        });

        return processedItem;
      });
    });
    return result;
  };

  useEffect(() => {
    if (!productData || productData.length === 0) {
      setProductDataMap({});
      return;
    }
    const generatedProductDataMap = {};
    productData.forEach((productDiff) => {
      const orchestrator = productDiff.orchestrator || {};

      // Pre-process the scenarios to hoist the step definitions
      const scenariosByType = { new: [], changed: [], deleted: [], same: [] };
      const allScenarios = (orchestrator.productFulfillmentScenarios?.new || []).concat(
        orchestrator.productFulfillmentScenarios?.changed || [],
        orchestrator.productFulfillmentScenarios?.deleted || [],
        orchestrator.productFulfillmentScenarios?.same || []
      );

      allScenarios.forEach((scenario) => {
        const group = scenario.fulfillmentStepDefinitionGroup?.[0];
        const hoistedScenario = {
          ...scenario,
          fulfillmentStepDefinitions: group?.fulfillmentStepDefinitions || {
            new: [],
            changed: [],
            deleted: [],
            same: [],
          },
        };
        scenariosByType[scenario.type]?.push(hoistedScenario);
      });

      generatedProductDataMap[productDiff.product.sfId || productDiff.product.id] = {
        productName: productDiff.product.name,
        scenarios: processDiffArray(scenariosByType, 'ProductFulfillmentScenario'),
      };
    });
    setProductDataMap(generatedProductDataMap);
    if (productData.length > 0) {
      setSelectedProductId(productData[0].product.sfId || productData[0].product.id);
    }
  }, [productData, visibilityConfig]);

  const handleToggleExpand = (id) => {
    setExpandedStates((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderExpandedContent = (row) => {
    const isExpanded = !!expandedStates[row.id];

    // --- THIS IS THE FIX ---
    // Find ALL nested arrays on the current row object to be rendered.
    const nestedArrays = Object.entries(row).filter(
      ([key, value]) => value && typeof value === 'object' && value.hasOwnProperty('new')
    );

    return (
      <TableRow>
        <TableCell colSpan={columns.length} style={{ padding: '0px' }}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <ArgonBox p={3} bgcolor={grey[50]}>
              <DiffRenderer
                changes={row.diffs}
                sObjectName={'ProductFulfillmentScenario'}
                orgType={selectedOrg.type.toLowerCase()}
                showSameFields={showSameFields}
              />
              {/* Render ALL found nested item groups */}
              {nestedArrays.map(([key, value]) => {
                const nestedSObjectName =
                  {
                    fulfillmentStepDefinitions: 'FulfillmentStepDefinition',
                    // Add other explicit mappings if needed
                  }[key] || key.charAt(0).toUpperCase() + key.slice(1);

                return (
                  <NestedItemGroup
                    key={key}
                    title={key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                    items={value}
                    sObjectName={nestedSObjectName}
                    expandedStates={expandedStates}
                    onToggle={handleToggleExpand}
                    parentOrg={selectedOrg}
                    showSameFields={showSameFields}
                  />
                );
              })}
            </ArgonBox>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  const renderProductSection = (productName, data) => {
    const { scenarios } = data;
    const renderTableSection = (title, rows) => {
      if (!rows || rows.length === 0) return null;
      const formattedRows = rows.map((row) => ({
        ...row,
        item: (
          <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
            {row.item}
          </ArgonTypography>
        ),
        onClick: () => handleToggleExpand(row.id),
        selected: !!expandedStates[row.id],
        actions: (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleExpand(row.id);
            }}
          >
            <Icon>{expandedStates[row.id] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
          </IconButton>
        ),
      }));
      return (
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                {title}
              </ArgonTypography>
              <StageExpandableTable
                rows={formattedRows}
                columns={columns}
                renderRowSubComponent={({ row }) => renderExpandedContent(row)}
              />
            </ArgonBox>
          </Card>
        </ArgonBox>
      );
    };
    const hasAnyData = ['new', 'changed', 'deleted', 'same'].some((type) => scenarios[type]?.length > 0);
    if (!hasAnyData)
      return (
        <Card>
          <ArgonBox p={3} textAlign="center">
            <ArgonTypography>No fulfillment data available.</ArgonTypography>
          </ArgonBox>
        </Card>
      );
    return (
      <ArgonBox key={productName}>
        {['new', 'changed', 'deleted', 'same'].map(
          (type) =>
            scenarios[type]?.length > 0 &&
            renderTableSection(`${type.charAt(0).toUpperCase() + type.slice(1)} Fulfillment Scenarios`, scenarios[type])
        )}
      </ArgonBox>
    );
  };

  const productList = Object.keys(productDataMap).map((id) => ({ id, name: productDataMap[id].productName }));
  const handleProductSelect = (productId) => {
    setSelectedProductId(productId);
    setExpandedStates({});
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <ProductList
          products={productList}
          selectedProductId={selectedProductId}
          onProductSelect={handleProductSelect}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        {selectedProductId && productDataMap[selectedProductId] ? (
          renderProductSection(productDataMap[selectedProductId].productName, productDataMap[selectedProductId])
        ) : (
          <Card>
            <ArgonBox p={3} textAlign="center">
              <ArgonTypography>Select a product to view fulfillment details.</ArgonTypography>
            </ArgonBox>
          </Card>
        )}
      </Grid>
    </Grid>
  );
}

FulfillmentReview.propTypes = {
  productData: PropTypes.array.isRequired,
  showSameFields: PropTypes.bool,
  selectedOrg: PropTypes.object.isRequired,
};

export default FulfillmentReview;
